import React from 'react';
import {
  Controller,
  FormProvider,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import {
  Button,
  LoadingButton,
  ModalHeader,
  ModalBody,
  ModalFooter,
  TextField,
  Typography,
} from '@one-thd/sui-atomic-components';
import { yupResolver } from '@hookform/resolvers/yup';
import { IModalBasicProps } from '../../../models/common/modal';
import { CardType } from '../../../constants/payments';
import {
  IPaymentCardCommonData,
  TPaymentNicknameFormValues,
} from '../../../models/common/payments';
import { getTextFieldStatus } from '../../../utils/form';
import { IMAGES_ASSETS_URL } from '../../../constants/links';
import { GiftCardLogoAndNumber } from '../gift-card/logo-number';
import { getFormattedKeytag } from '../../../utils/payments';
import { validationSchema } from './validation';

export type TNicknameChangeAcceptedCardTypes =
  | CardType.GIFT_CARD
  | CardType.PAYPAL_ACCOUNT
  | CardType.PRO_XTRA_DOLLARS
  | CardType.CHECKING_ACCOUNT
  | CardType.KEY_TAG;

const headerMappings: Record<
  TNicknameChangeAcceptedCardTypes,
  (card: IPaymentCardCommonData) => string
> = {
  [CardType.GIFT_CARD]: () => {
    return 'Add/Edit Nickname';
  },
  [CardType.PAYPAL_ACCOUNT]: () => 'Add/Edit Nickname',
  [CardType.PRO_XTRA_DOLLARS]: () => 'Edit Nickname',
  [CardType.CHECKING_ACCOUNT]: () => 'Add/Edit Nickname',
  [CardType.KEY_TAG]: () => 'Add/Edit Nickname',
};

const buttonMappings: Record<
  TNicknameChangeAcceptedCardTypes,
  (card: IPaymentCardCommonData) => string
> = {
  [CardType.GIFT_CARD]: () => {
    return 'Save';
  },
  [CardType.PAYPAL_ACCOUNT]: () => 'Save',
  [CardType.PRO_XTRA_DOLLARS]: () => 'Save',
  [CardType.CHECKING_ACCOUNT]: () => 'Save',
  [CardType.KEY_TAG]: () => 'Save',
};

const fieldLabelMappings: Record<
  TNicknameChangeAcceptedCardTypes,
  (card: IPaymentCardCommonData) => string
> = {
  [CardType.GIFT_CARD]: () => {
    return 'Gift Card Nickname';
  },
  [CardType.PAYPAL_ACCOUNT]: () => 'PayPal Nickname',
  [CardType.PRO_XTRA_DOLLARS]: () => 'Nickname',
  [CardType.CHECKING_ACCOUNT]: () => 'Account Nickname',
  [CardType.KEY_TAG]: () => 'Keytag Nickname',
};

const childrenMapping: Record<
  TNicknameChangeAcceptedCardTypes,
  (card: IPaymentCardCommonData) => React.ReactNode
> = {
  [CardType.GIFT_CARD]: (card) => {
    return (
      <Typography variant="body-base">
        <div className="sui-flex sui-flex-wrap sui-py-4">
          <GiftCardLogoAndNumber card={card} />
        </div>
      </Typography>
    );
  },
  [CardType.PAYPAL_ACCOUNT]: () => null,
  [CardType.PRO_XTRA_DOLLARS]: (card) => {
    const { cardNumberLast4, rewardExpDate } = card;

    return (
      <div className="sui-flex sui-mb-6">
        <img
          width={40}
          height="auto"
          className="sui-inline-block sui-mr-4"
          src={`${IMAGES_ASSETS_URL}/pro-xtra-rewards.png`}
          alt="ProXtra card logo"
        />
        <div className="sui-mr-4">
          <Typography variant="body-lg" color="subtle">
            -{cardNumberLast4}-
          </Typography>
        </div>
        <Typography variant="body-lg" color="subtle">
          {`Exp ${rewardExpDate}`}
        </Typography>
      </div>
    );
  },
  [CardType.CHECKING_ACCOUNT]: (card) => {
    const { cardNumberLast4 } = card;
    return (
      <div className="sui-pb-6">
        <Typography variant="body-lg">Account ****{cardNumberLast4}</Typography>
      </div>
    );
  },
  [CardType.KEY_TAG]: (card) => {
    return (
      <div className="sui-pb-6">
        <Typography variant="body-lg">{getFormattedKeytag(card)}</Typography>
      </div>
    );
  },
};

export interface IEditCardNicknameModalProps {
  card: IPaymentCardCommonData;
  cardType: TNicknameChangeAcceptedCardTypes;
  onConfirmEdit: (cardNickName: string) => void;
  isLoading: boolean;
}

export const EditCardNicknameModal = ({
  card,
  cardType,
  hideModal,
  onConfirmEdit,
  isLoading,
}: IEditCardNicknameModalProps & IModalBasicProps) => {
  const defaultValues = {
    cardNickName: card.cardNickName || '',
  };

  const formMethods = useForm<TPaymentNicknameFormValues>({
    defaultValues,
    resolver: yupResolver(validationSchema),
    mode: 'onTouched',
  });

  const {
    control,
    formState: { errors: formErrors, dirtyFields },
    handleSubmit,
  } = formMethods;

  const onSubmitForm: SubmitHandler<TPaymentNicknameFormValues> = ({
    cardNickName,
  }) => {
    if (dirtyFields.cardNickName) {
      onConfirmEdit(cardNickName || '');
    } else {
      hideModal();
    }
  };

  const headerText = headerMappings[cardType](card);
  const buttonText = buttonMappings[cardType](card);
  const labelText = fieldLabelMappings[cardType](card);
  const children = childrenMapping[cardType](card);
  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmitForm)}>
        <ModalHeader onClose={hideModal}>{headerText}</ModalHeader>
        <ModalBody>
          {children}
          <Controller
            name="cardNickName"
            control={control}
            render={({ field }) => {
              const { ref, value, name, onChange, ...rest } = field;
              return (
                <TextField
                  id="tenderNickNameField"
                  optional
                  fullWidth
                  label={labelText}
                  type="text"
                  status={getTextFieldStatus(formErrors.cardNickName)}
                  statusMessage={formErrors.cardNickName?.message}
                  value={value || ''}
                  name={name}
                  onChange={onChange}
                  InputProps={{
                    inputProps: { ...rest, 'data-testid': 'nicknameField' },
                  }}
                />
              );
            }}
          />
        </ModalBody>
        <ModalFooter position="end">
          <div className="sui-flex sui-gap-4 sm:sui-gap-4 sui-flex-col-reverse sui-w-full sm:sui-flex-row sm:sui-w-auto">
            <Button variant="secondary" onClick={hideModal}>
              Cancel
            </Button>
            <LoadingButton
              loading={isLoading}
              variant="primary"
              type="submit"
              data-testid="submitBtn"
            >
              {buttonText}
            </LoadingButton>
          </div>
        </ModalFooter>
      </form>
    </FormProvider>
  );
};

EditCardNicknameModal.displayName = 'EditCardNicknameModal';
